/**
 * The live pre-flight (D13): what a floor manager checks before the red
 * light — the door open, the channel and its key, the host's own picture in
 * the room, who is on air, the rig. Pure over the console's state, so the
 * wording is tested; every line names the fix.
 */
export interface LiveState {
  room: { status: string; channel_id: string | null; egress_id?: string | null } | null;
  channel: { ingest_url: string; status: string; rig: unknown } | null;
  media: boolean;
  joined: boolean;
  guests: Array<{ status: string }>;
}
export interface LiveCheck { ok: boolean; says: string; fix?: string }

export function livePreflight(s: LiveState): LiveCheck[] {
  if (!s.room) return [{ ok: false, says: 'no room is open', fix: 'Open a room — it is the show\'s door' }];
  const out: LiveCheck[] = [];
  out.push(s.room.status === 'closed'
    ? { ok: false, says: 'the door is closed', fix: 'reopen the room so guests can arrive' }
    : { ok: true, says: 'the door is open' });
  out.push(s.media ? { ok: true, says: 'the media server carries the room' }
    : { ok: false, says: 'no media server — the door works, nobody is seen or heard', fix: 'set LIVEKIT_URL, LIVEKIT_API_KEY and LIVEKIT_API_SECRET on the API' });
  out.push(s.joined ? { ok: true, says: 'you are in the room, on camera' }
    : { ok: false, says: 'you have not joined the room\'s media', fix: 'Join the room (camera + mic)' });
  const onAir = s.guests.filter((g) => g.status === 'on_air').length;
  const waiting = s.guests.filter((g) => g.status === 'waiting' || g.status === 'held').length;
  out.push(onAir > 0 ? { ok: true, says: `${onAir} guest${onAir === 1 ? '' : 's'} on air` }
    : waiting > 0 ? { ok: false, says: `${waiting} waiting at the door, nobody on air`, fix: 'Admit, then Bring up' }
    : { ok: true, says: 'no guests — a solo show' });
  if (!s.room.channel_id) out.push({ ok: false, says: 'the room has no channel — it will not go out anywhere', fix: 'pick a channel on the room, or make one under Channels' });
  else if (!s.channel) out.push({ ok: false, says: 'the room\'s channel no longer exists', fix: 'pick another channel on the room' });
  else {
    out.push(s.channel.ingest_url ? { ok: true, says: `the channel points at ${host(s.channel.ingest_url)}` }
      : { ok: false, says: 'the channel has no ingest URL', fix: 'set it on Channels, from the platform\'s Go live page' });
    out.push(s.channel.rig ? { ok: true, says: 'the rig is on — badge, ticker, mark' }
      : { ok: false, says: 'no rig — the stream goes out bare', fix: 'give the channel a rig on Channels' });
  }
  if (s.room.egress_id) out.push({ ok: true, says: 'ON AIR now' });
  return out;
}

function host(url: string): string {
  try { return new URL(url.replace(/^rtmps?:\/\//, 'https://')).host || url; } catch { return url; }
}
